<template>
  <div class="notice-detail resource-detail">
    <div class="notice-header resource-header">
      <h2 class="notice-title resource-title">{{ resource.title }}</h2>
      <div class="notice-meta resource-meta">
        <span class="meta-item">작성자 : 관리자 </span>
        <span class="meta-item">{{ formatDate(resource.createdAt) }}</span>
        <div v-if="hasFiles" class="file-dropdown-wrapper">
          <button class="file-dropdown-btn" @click="toggleDropdown">
            첨부파일
            <span :class="{'arrow-up': showFiles, 'arrow-down': !showFiles}"></span>
          </button>
          <transition name="dropdown">
            <ul v-show="showFiles" class="file-dropdown-list">
              <li v-for="(url, name) in resource.fileUrl" :key="name">
                <a
                  :href="getDownloadLink(url)"
                  :download="name"
                  target="_blank"
                  class="file-link"
                >
                  <span class="file-icon">{{ getFileIcon(name) }}</span>
                  <span class="file-name">{{ name }}</span>
                  <span class="file-tooltip" role="tooltip">{{ name }}</span>
                </a>
              </li>
            </ul>
          </transition>
        </div>
      </div>
    </div>
    
    <div class="notice-content resource-content" v-html="resource.content"></div>

    <div class="button-area">
      <router-link to="/notice/resources" class="list-button">목록</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import axios from '@/utils/axios';

const resource = ref({});
const showFiles = ref(false);

const route = useRoute();

const getFileIcon = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf': return '📄';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return '🖼';
    case 'xlsx':
    case 'xls':
      return '📊';
    case 'doc':
    case 'docx':
      return '📃';
    case 'zip':
    case '7z':
    case 'rar':
      return '🗜';
    case 'hwp':
      return '📝';
    default: return '📁';
  }
};

onMounted(() => {
  const resourceId = route.params.id;
  axios.get(`/bx201/resource/${resourceId}`)
    .then((response) => {
      resource.value = response.data;
    })
    .catch(() => {});
});

const hasFiles = computed(() =>
  resource.value.fileUrl && Object.keys(resource.value.fileUrl).length > 0
);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDownloadLink = (filePath) => {
  // filePath가 'C:\\Achacha_Dev\\resources\\e2fc5ea1-865e-4330-a4ec-b1957f64a28e_05_자산보유 사실확인서.pdf'
  // 일 때, 실제 파일명만 추출 (윈도우 \, 리눅스 / 모두 대응)
  const filename = filePath.split(/\\|\//).pop();
  // 다운로드 URL 인코딩
  return `/bx201/download/${encodeURIComponent(filename)}`;
};

const toggleDropdown = () => {
  showFiles.value = !showFiles.value;
};
</script>

<style scoped>
.notice-detail,
.resource-detail {
  max-width: 1200px;
  margin: 50px auto;
  padding: 0;
  background-color: #fff;
  font-family: 'Noto Sans KR', sans-serif;
}

.notice-header,
.resource-header {
  border-top: 2px solid #333;
  border-bottom: 1px solid #ddd;
  padding: 20px 15px;
}

.notice-title,
.resource-title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0 0 15px 0;
}

.notice-meta,
.resource-meta {
  display: flex;
  align-items: center;
  color: #888;
  font-size: 14px;
  position: relative;
}

.meta-item { margin-right: 20px; }

/* ───────── 드롭다운 버튼 영역 ───────── */
.file-dropdown-wrapper { margin-left:auto; position:relative; min-width:100px; z-index:10; }
.file-dropdown-btn {
  background:#eee; border:none; color:#444;
  padding:6px 18px 6px 12px; border-radius:4px;
  font-size:14px; font-weight:500; cursor:pointer; outline:none;
  transition:background .2s;
}
.file-dropdown-btn:hover { background:#e0e0e0; }

.arrow-down, .arrow-up {
  display:inline-block; margin-left:6px; width:0; height:0;
  border-left:6px solid transparent; border-right:6px solid transparent; vertical-align:middle;
}
.arrow-down { border-top:7px solid #666; }
.arrow-up   { border-bottom:7px solid #666; }

/* ───────── 드롭다운 리스트 ───────── */
.file-dropdown-list {
  list-style:none;
  position:absolute; top:36px; right:0;
  background:#fff; border:1px solid #ddd; border-radius:8px;
  width:auto; min-width:120px; max-width:360px;
  padding:10px 0; margin:0; z-index:100;
  box-shadow:0 2px 10px rgba(0,0,0,.13);
}
.file-dropdown-list li { margin:0; padding:0; }

/* 링크: flex + 툴팁 기준점 */
.file-dropdown-list a.file-link {
  position: relative;
  display:flex; align-items:center; gap:8px;
  color:#007bff;
  padding:7px 24px 7px 16px;
  text-decoration:none; font-size:.97rem;
  transition:background .18s, color .18s;
  max-width:320px;
  white-space:nowrap;
  overflow:visible;          /* 툴팁이 밖으로 나올 수 있게 */
  text-overflow:clip;
}
.file-dropdown-list a.file-link:hover { background:#f2f6fa; color:#0056b3; }

/* 아이콘 */
.file-icon { font-size:1.13em; margin-right:4px; min-width:1.4em; text-align:center; }

/* 파일명: flex 환경에서 말줄임 */
.file-name {
  flex:1;
  min-width:0;              /* ⬅️ 핵심: 수축 허용 */
  overflow:hidden;
  white-space:nowrap;
  text-overflow:ellipsis;   /* ⬅️ 말줄임 */
  vertical-align:middle;
}

/* 커스텀 툴팁: 기본 숨김 (위쪽으로 띄움) */
.file-tooltip {
  position:absolute;
  left:16px;                 /* 아이콘 오른쪽에서 시작 */
  bottom:calc(100% + 8px);   /* 링크 위쪽으로 */
  max-width:540px;
  padding:8px 10px;
  background:#fff; color:#333; font-size:.95rem;
  border:1px solid #ddd; border-radius:8px;
  box-shadow:0 2px 10px rgba(0,0,0,.12);
  white-space:nowrap; z-index:1000;

  pointer-events:none;       /* 깜빡임 방지 */
  opacity:0; transform:translateY(4px);
  transition:opacity .12s ease, transform .12s ease;
  display:block;             /* 애니메이션을 위해 block 유지 */
}
/* 호버 시 툴팁 표시 */
.file-link:hover .file-tooltip { opacity:1; transform:translateY(0); }

/* 드롭다운 트랜지션 */
.dropdown-enter-active, .dropdown-leave-active { transition:opacity .18s; }
.dropdown-enter-from, .dropdown-leave-to { opacity:0; }
.dropdown-enter-to, .dropdown-leave-from { opacity:1; }

/* 본문 */
.notice-content, .resource-content {
  min-height:300px; padding:30px 15px; line-height:1.8;
  color:#555; font-size:15px; border-bottom:1px solid #ddd;
}

.button-area { margin-top:30px; text-align:center; }
.list-button {
  display:inline-block; padding:10px 30px; background:#666; color:#fff;
  text-decoration:none; font-size:14px; border-radius:0; transition:background-color .3s;
}
.list-button:hover { background:#444; }

/* 반응형 */
@media (max-width:768px) {
  .notice-detail, .resource-detail { margin:30px auto; padding:0 8px; }
  .notice-title, .resource-title { font-size:16px; }
  .notice-meta, .resource-meta { flex-direction:column; align-items:flex-start; }
  .file-dropdown-wrapper { margin:10px 0 0 0; width:100%; }
  .file-dropdown-btn { width:100%; text-align:left; }
  .file-dropdown-list {
    position:static; border-radius:4px; box-shadow:none;
    min-width:100%; width:100%; margin-top:5px;
  }
}

.notice-content img, .resource-content img {
  max-width:100%; height:auto; display:block;
  margin:10px auto; min-width:0;
}
</style>
